import React, { useEffect, useRef, useState } from 'react';

interface AudioControllable {
	volume: number;
	muted: boolean;
}

interface LocalAudio {
	localVolume: number;
	localMuted: boolean;
}

export default function useAudioControls(
	stream: AudioControllable | null | undefined,
	onUpdate?: (props: { volume: number; muted: boolean }) => void,
) {
	const volume = stream?.volume ?? 0;
	const muted = stream?.muted ?? false;

	const [audio, setAudio] = useState<LocalAudio>({ localVolume: volume, localMuted: muted });
	const { localVolume, localMuted } = audio;

	const volumeDebounce = useRef<NodeJS.Timeout | null>(null);

	const handleMuteChange = () => {
		const newMuted = !localMuted;
		setAudio({ localVolume, localMuted: newMuted });
		onUpdate?.({ volume: localVolume, muted: newMuted });
	};

	const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newVolume = parseFloat(e.target.value);
		setAudio({ localVolume: newVolume, localMuted: false });
		if (volumeDebounce.current) { clearTimeout(volumeDebounce.current); }
		volumeDebounce.current = setTimeout(() => {
			onUpdate?.({ volume: newVolume, muted: false });
		}, 100);
	};

	useEffect(() => {
		if (volume !== localVolume || muted !== localMuted) {
			setAudio({ localVolume: volume, localMuted: muted });
		}
	}, [volume, muted]);

	return {
		localVolume,
		localMuted,
		handleMuteChange,
		handleVolumeChange,
	};
}
